import type { CategoryDef, ScoreContext } from '../types';
import {
  containsAll,
  highestOfAKind,
  isFullHouse,
  longestRun,
  sum,
  sumOf,
} from '../dice';

export const FACE_NAMES = [
  '',
  'Ones',
  'Twos',
  'Threes',
  'Fours',
  'Fives',
  'Sixes',
];

export function upperCategory(value: number, hintSuffix = ''): CategoryDef {
  return {
    id: `n${value}`,
    name: FACE_NAMES[value] ?? String(value),
    short: String(value),
    section: 'upper',
    hint: `Sum of all ${value}s${hintSuffix}`,
    score: ctx => sumOf(ctx.dice, value),
  };
}

export function upperCategories(hintSuffix = ''): CategoryDef[] {
  return [1, 2, 3, 4, 5, 6].map(v => upperCategory(v, hintSuffix));
}

export const UPPER_IDS = ['n1', 'n2', 'n3', 'n4', 'n5', 'n6'];

/** Sum of all dice if at least n of a kind, else 0. */
export function ofAKindAll(
  id: string,
  name: string,
  short: string,
  n: number
): CategoryDef {
  return {
    id,
    name,
    short,
    section: 'lower',
    hint: `${n} of a kind: total of all dice`,
    score: ctx => (highestOfAKind(ctx.dice, n) > 0 ? sum(ctx.dice) : 0),
  };
}

/** Sum of only the matching dice (Yatzy-style). */
export function ofAKindOnly(
  id: string,
  name: string,
  short: string,
  n: number
): CategoryDef {
  return {
    id,
    name,
    short,
    section: 'lower',
    hint: `${n} of a kind: sum of those ${n} dice`,
    score: ctx => highestOfAKind(ctx.dice, n) * n,
  };
}

export function fixedCategory(
  id: string,
  name: string,
  short: string,
  hint: string,
  points: number,
  test: (ctx: ScoreContext) => boolean
): CategoryDef {
  return {
    id,
    name,
    short,
    section: 'lower',
    hint,
    score: ctx => (test(ctx) ? points : 0),
  };
}

export const classicFullHouse: CategoryDef = {
  id: 'fullHouse',
  name: 'Full House',
  short: 'FH',
  section: 'lower',
  hint: 'Three of one number and two of another: 25',
  score: ctx => (ctx.joker || isFullHouse(ctx.dice) ? 25 : 0),
};

export const classicSmallStraight: CategoryDef = {
  id: 'smallStraight',
  name: 'Small Straight',
  short: 'Sm',
  section: 'lower',
  hint: 'Any four numbers in a row, e.g. 2-3-4-5: 30',
  score: ctx => (ctx.joker || longestRun(ctx.dice) >= 4 ? 30 : 0),
};

export const classicLargeStraight: CategoryDef = {
  id: 'largeStraight',
  name: 'Large Straight',
  short: 'Lg',
  section: 'lower',
  hint: 'Five numbers in a row, 1-2-3-4-5 or 2-3-4-5-6: 40',
  score: ctx => (ctx.joker || longestRun(ctx.dice) >= 5 ? 40 : 0),
};

export const chance: CategoryDef = {
  id: 'chance',
  name: 'Chance',
  short: 'Ch',
  section: 'lower',
  hint: 'Anything goes: the total of all five dice',
  score: ctx => sum(ctx.dice),
};

export function fiveKindCategory(
  id: string,
  name: string,
  short: string,
  points: number
): CategoryDef {
  return fixedCategory(
    id,
    name,
    short,
    `Five of a kind (all five dice the same number): ${points}`,
    points,
    ctx => highestOfAKind(ctx.dice, 5) > 0
  );
}

export function straightExact(
  id: string,
  name: string,
  short: string,
  values: number[],
  points: number
): CategoryDef {
  return fixedCategory(
    id,
    name,
    short,
    `${values.join('-')}: ${points}`,
    points,
    ctx => containsAll(ctx.dice, values)
  );
}

export function thresholdBonus(threshold: number, bonus: number) {
  return (upper: number) => (upper >= threshold ? bonus : 0);
}

export const noBonus = () => 0;
